import * as Comlink from 'comlink';
import type { Heightmap, NoiseParams } from '@wg/noise';
import type { NoiseWorkerApi } from '@/workers/noise.worker.js';

let instance: Comlink.Remote<NoiseWorkerApi> | null = null;

function getClient(): Comlink.Remote<NoiseWorkerApi> {
  if (instance) return instance;
  const worker = new Worker(new URL('@/workers/noise.worker.ts', import.meta.url), { type: 'module' });
  instance = Comlink.wrap<NoiseWorkerApi>(worker);
  return instance;
}

export async function generate(params: NoiseParams): Promise<Heightmap> {
  return getClient().generate(params);
}

// Debounced variant — cancels in-flight if params change within `delay`.
export function makeDebouncedGenerate(delay = 150) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let token = 0;
  return (params: NoiseParams): Promise<Heightmap> =>
    new Promise((resolve, reject) => {
      const myToken = ++token;
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        try {
          const result = await generate(params);
          if (myToken !== token) return; // superseded
          resolve(result);
        } catch (e) {
          if (myToken === token) reject(e);
        }
      }, delay);
    });
}

// Module-level shared debouncer — use this from hooks/subscriptions so a single
// process-wide queue coalesces rapid param changes. Tests may still call the
// factory directly to get an isolated instance.
export const debouncedGenerate = makeDebouncedGenerate(150);
